/*
 * "Ask us a question": the shopper's chat with us (Cal 2026-10-19: "You can answer the chat, but there should be no
 * phone number"). No Shopify app: the back end is the mvsport-chat worker (launchpad task shopmvsport-chat, chat/),
 * and the operator's sessions answer it (a monitor wakes one when a question waits). The bubble shows on the Studio's
 * first screen and on group-link pages, and on every Studio screen once the shopper has a conversation.
 * The open panel polls for our reply every 8 s; a closed panel peeks once a minute for the unread dot (a peek does not
 * count as read, so the "email the answer" follow-up still fires when the shopper never opened it).
 * Our own browsers (?internal=1) may use the test worker with ?chat=next.
 */
(function () {
  if (window.MvChat) return;
  // WAIT_MS: a closed panel whose question is not answered yet peeks more often, so the dot comes soon after the answer.
  const VISITOR = 'mv_chat_visitor', CONV = 'mv_chat_conv', OPEN_MS = 8000, PEEK_MS = 60000, WAIT_MS = 20000;
  const store = {
    get(k) { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch (_) { return null; } },
    set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (_) { /* storage blocked: the chat lives for this page only */ } },
  };
  const base = (() => {
    try {
      const q = new URLSearchParams(location.search), internal = q.get('internal') === '1' || localStorage.getItem('mv_internal') === '1';
      if (internal && (q.get('chat') || sessionStorage.getItem('mv_chat_env')) === 'next') { sessionStorage.setItem('mv_chat_env', 'next'); return 'https://mvsport-chat-next.cf.dpeyserapps.com'; }
    } catch (_) {}
    return 'https://mvsport-chat.cf.dpeyserapps.com';
  })();
  const track = (name, props) => { try { if (window.mvTrack) window.mvTrack(name, props || {}); } catch (_) {} };
  const visitor = (() => {
    let v = store.get(VISITOR);
    if (!v) { v = 'v' + Math.random().toString(36).slice(2, 12) + Date.now().toString(36); store.set(VISITOR, v); }
    return v;
  })();
  const h = (tag, attrs, kids) => {
    const n = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs || {})) {
      if (k === 'text') n.textContent = v; else if (k.startsWith('on')) n.addEventListener(k.slice(2), v); else if (v !== false && v != null) n.setAttribute(k, v === true ? '' : v);
    }
    (kids || []).forEach((c) => n.append(c));
    return n;
  };
  // Our answers name Studio links ("shopmvsport.com/collections/…"): make our own site's links tappable, nothing else.
  const linked = (text) => {
    const out = [], re = /(?:https?:\/\/)?(?:www\.)?shopmvsport\.com(?:\/[^\s]*[^\s.,;:!?)])?/g;
    let i = 0, m;
    while ((m = re.exec(text))) {
      out.push(text.slice(i, m.index));
      out.push(h('a', { href: m[0].startsWith('http') ? m[0] : `https://${m[0]}`, text: m[0] }));
      i = m.index + m[0].length;
    }
    out.push(text.slice(i));
    return out;
  };
  const time = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); } catch (_) { return ''; } };

  let conv = store.get(CONV); // {id, key, msgs:[{id, role, at, text}], email}
  let open = false, timer = null, sending = false, root, panel, list, form, input, emailRow, emailIn, bubble, label, dot, status, ask, askIn, askMsg;

  function build() {
    dot = h('span', { class: 'mvc-dot', hidden: true, 'aria-label': 'New reply' });
    label = h('span', { text: 'Ask us a question' });
    bubble = h('button', { type: 'button', class: 'mvc-bubble', 'aria-expanded': 'false', 'aria-controls': 'mvc-panel', onclick: () => toggle(true) }, [label, dot]);
    // After a question goes without an email (sweep 2026-10-21 bug 13: the one real question was answered 7 minutes later
    // and nobody read it; the shopper had closed the chat 11 s after sending): one line asks for the email.
    askIn = h('input', { class: 'mvc-email', type: 'email', autocomplete: 'email', placeholder: 'you@example.com', 'aria-label': 'Your email for the answer' });
    askMsg = h('span', { class: 'mvc-fine' });
    ask = h('form', { class: 'mvc-ask', hidden: true, onsubmit: (e) => { e.preventDefault(); saveEmail(); } }, [
      h('p', { class: 'mvc-ask__t', text: 'Want the answer by email too? We usually reply within 15 minutes.' }),
      h('div', { class: 'mvc-ask__row' }, [askIn, h('button', { type: 'submit', class: 'mvc-send', text: 'Email me' })]), askMsg,
    ]);
    list = h('div', { class: 'mvc-list', 'aria-live': 'polite' });
    input = h('textarea', { class: 'mvc-input', rows: '2', maxlength: '1500', placeholder: 'Type your question', 'aria-label': 'Your question' });
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } });
    emailIn = h('input', { class: 'mvc-email', type: 'email', autocomplete: 'email', placeholder: 'Your email (optional)', 'aria-label': 'Your email, optional' });
    emailRow = h('div', { class: 'mvc-emailrow' }, [emailIn]);
    status = h('p', { class: 'mvc-status', role: 'status' });
    form = h('form', { class: 'mvc-form', onsubmit: (e) => { e.preventDefault(); send(); } }, [input, emailRow, h('div', { class: 'mvc-row' }, [status, h('button', { type: 'submit', class: 'mvc-send', text: 'Send' })])]);
    panel = h('section', { id: 'mvc-panel', class: 'mvc-panel', role: 'dialog', 'aria-label': 'Ask us a question', hidden: true }, [
      h('header', { class: 'mvc-head' }, [h('b', { text: 'Ask us a question' }), h('button', { type: 'button', class: 'mvc-close', 'aria-label': 'Close', text: '×', onclick: () => toggle(false) })]),
      h('p', { class: 'mvc-note', text: 'We reply here, usually within 15 minutes, 9 to 5 ET. Leave your email if you want the answer by email too.' }),
      list, ask, form,
    ]);
    root = h('div', { class: 'mvc', hidden: true }, [panel, bubble]);
    document.body.append(root);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && open) toggle(false); });
    document.addEventListener('click', (e) => { const t = e.target.closest && e.target.closest('[data-mv-chat-open]'); if (t) { e.preventDefault(); toggle(true); } });
  }

  function paint() {
    list.replaceChildren(...((conv && conv.msgs) || []).map((m) => h('div', { class: `mvc-msg mvc-msg--${m.role === 'us' ? 'us' : 'me'}` }, [
      h('p', {}, m.role === 'us' ? linked(m.text) : [m.text]), h('span', { class: 'mvc-meta', text: `${m.role === 'us' ? 'MV Sport' : 'You'} · ${time(m.at)}` }),
    ])));
    const waiting = conv && conv.msgs && conv.msgs.length && conv.msgs[conv.msgs.length - 1].role !== 'us';
    if (waiting && !sending) status.textContent = 'Sent. We reply here.';
    emailRow.hidden = !!(conv && conv.email);
    ask.hidden = !(waiting && !conv.email);
    list.hidden = !(conv && conv.msgs && conv.msgs.length);
    paintBubble();
    list.scrollTop = list.scrollHeight;
  }

  // The closed bubble says where the question stands: waiting for our answer, or answered (with the dot).
  function paintBubble() {
    const last = conv && conv.msgs && conv.msgs.length ? conv.msgs[conv.msgs.length - 1] : null;
    label.textContent = !dot.hidden ? 'Your answer is here' : last && last.role !== 'us' ? 'We are answering…' : 'Ask us a question';
  }
  async function saveEmail() {
    const e = askIn.value.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e)) { askMsg.textContent = 'That email does not look right.'; askIn.focus(); return; }
    askMsg.textContent = 'Saving…';
    try {
      const r = await fetch(`${base}/api/chat/${conv.id}/email?key=${encodeURIComponent(conv.key)}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: e }) });
      if (!r.ok) throw new Error(String(r.status));
      conv.email = true; store.set(CONV, conv);
      askMsg.textContent = ''; track('chat_email_added', { after_send: true });
      status.textContent = 'Thanks. We reply here and by email.';
      paint();
    } catch (_) { askMsg.textContent = 'That did not save. Try again.'; }
  }

  function toggle(to) {
    open = to;
    panel.hidden = !to;
    bubble.setAttribute('aria-expanded', String(to));
    root.classList.toggle('is-open', to);
    showRoot(); // the header's "Chat with us" pill opens it on pages where the bubble is not shown (redesign part 1)
    if (to) { dot.hidden = true; paint(); input.focus({ preventScroll: true }); track('chat_opened', { has_conversation: !!conv }); poll(); }
    paintBubble();
    schedule();
  }

  function schedule() {
    clearTimeout(timer);
    if (!conv) return;
    const last = conv.msgs && conv.msgs.length ? conv.msgs[conv.msgs.length - 1] : null;
    timer = setTimeout(poll, open ? OPEN_MS : last && last.role !== 'us' ? WAIT_MS : PEEK_MS);
  }

  async function poll() {
    if (!conv) return;
    clearTimeout(timer);
    try {
      const after = conv.msgs.length ? conv.msgs[conv.msgs.length - 1].id : 0;
      const r = await fetch(`${base}/api/chat/${conv.id}?key=${encodeURIComponent(conv.key)}&after=${after}${open ? '' : '&peek=1'}`, { cache: 'no-store' });
      if (r.status === 404) { conv = null; store.set(CONV, null); paint(); return; }
      const j = await r.json();
      if (j.email && !conv.email) { conv.email = true; store.set(CONV, conv); emailRow.hidden = true; }
      if (j.messages && j.messages.length) {
        conv.msgs = conv.msgs.concat(j.messages.filter((m) => !conv.msgs.some((x) => x.id === m.id)));
        store.set(CONV, conv);
        if (j.messages.some((m) => m.role === 'us')) { status.textContent = ''; if (!open) { dot.hidden = false; root.hidden = false; } else track('chat_reply_seen', {}); }
        paint();
      }
    } catch (_) { /* offline for a moment: the next poll tries again */ }
    schedule();
  }

  async function send() {
    const text = input.value.trim(), email = emailIn.value.trim();
    if (!text || sending) return;
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) { status.textContent = 'That email does not look right.'; emailIn.focus(); return; }
    sending = true; status.textContent = 'Sending…';
    try {
      const r = await fetch(`${base}/api/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitor, page: location.pathname + location.search, text, email: email || undefined, id: conv && conv.id, key: conv && conv.key }),
      });
      const j = await r.json();
      if (!r.ok) { status.textContent = j.error ? `${j.error[0].toUpperCase()}${j.error.slice(1)}.` : 'That did not send. Try again.'; return; }
      const first = !conv || conv.id !== j.id;
      conv = { id: j.id, key: j.key, msgs: j.messages, email: (conv && conv.id === j.id && conv.email) || !!email };
      store.set(CONV, conv);
      input.value = ''; emailIn.value = '';
      track(first ? 'chat_started' : 'chat_message_sent', { with_email: !!email });
      sending = false; paint();
      status.textContent = 'Sent. We reply here.';
      showRoot();
    } catch (_) {
      status.textContent = 'That did not send. Check your connection and try again.';
    } finally { sending = false; schedule(); }
  }

  // Where the bubble shows: the Studio's first screen and group-link pages; anywhere in the Studio once the shopper
  // has a conversation (so the answer can reach them on any screen).
  function wanted() {
    const studio = document.querySelector('studio-v2');
    if (!studio) return !!conv;
    if (conv) return true;
    if (new URLSearchParams(location.search).get('group')) return true;
    return studio.dataset.screen === 'brief';
  }
  function showRoot() { if (root) root.hidden = !wanted() && !open; }

  function start() {
    build();
    showRoot();
    const studio = document.querySelector('studio-v2');
    if (studio) new MutationObserver(showRoot).observe(studio, { attributes: true, attributeFilter: ['data-screen'] });
    if (conv) poll();
  }
  window.MvChat = { open: () => toggle(true) };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
